import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile
} from 'firebase/auth';
import {
  BrowserRouter,
  Navigate,
  NavLink,
  Route,
  Routes,
  useNavigate,
  useParams
} from 'react-router-dom';
import {
  FiActivity,
  FiBarChart2,
  FiCheck,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiCopy,
  FiCornerDownRight,
  FiEdit2,
  FiGrid,
  FiHome,
  FiLogOut,
  FiMoon,
  FiRefreshCw,
  FiSearch,
  FiSend,
  FiPlay,
  FiUser,
  FiX,
  FiSun
} from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import { FaFacebookMessenger, FaInstagram, FaTelegramPlane, FaWhatsapp } from 'react-icons/fa';
import { countries } from 'countries-list';
import { geoNaturalEarth1, geoPath } from 'd3-geo';
import './App.css';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { getAppById, linkApps } from './data/linkApps';
import {
  generateCloudflareLink,
  getCloudflareLinkAnalytics,
  listCloudflareLinks,
  syncCloudflareUser
} from './cloudflareApi';
import { auth, googleProvider } from './firebase';

const appIcons = {
  whatsapp: FaWhatsapp,
  telegram: FaTelegramPlane,
  messenger: FaFacebookMessenger,
  instagram: FaInstagram
};

const COUNTRY_STORAGE_KEY = 'link-generator-country-v1';
const ANALYTICS_STORAGE_KEY = 'link-generator-analytics-cache-v1';
const LINK_OVERRIDES_STORAGE_KEY = 'link-generator-link-overrides-v1';
const WORLD_GEOJSON_URL = 'https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson';
const WORLD_MAP_WIDTH = 980;
const WORLD_MAP_HEIGHT = 470;

const regionNameFormatter =
  typeof Intl !== 'undefined' && Intl.DisplayNames ? new Intl.DisplayNames(['en'], { type: 'region' }) : null;

const countryCodeToFlag = (countryCode) =>
  countryCode
    .toUpperCase()
    .replace(/./g, (character) => String.fromCodePoint(127397 + character.charCodeAt()));

const getCountryMeta = (countryValue) => {
  const code = String(countryValue || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) {
    return { flag: '🌐', name: 'Unknown', code: '', flagUrl: '' };
  }

  const fallbackName = countries[code]?.name || code;

  return {
    flag: countryCodeToFlag(code),
    name: regionNameFormatter?.of(code) || fallbackName,
    code,
    flagUrl: `https://flagcdn.com/w40/${code.toLowerCase()}.png`
  };
};

const countryPhoneOptions = Object.entries(countries)
  .flatMap(([countryCode, country]) =>
    country.phone.map((dialCode) => ({
      value: `+${dialCode}`,
      name: country.name,
      flag: countryCodeToFlag(countryCode),
      flagUrl: `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`,
      label: `${countryCodeToFlag(countryCode)} ${country.name} (+${dialCode})`,
      countryCode
    }))
  )
  .sort((left, right) => left.label.localeCompare(right.label));

const parseWhatsappDetails = (originalLink) => {
  const fallback = {
    countryName: '---',
    countryCode: '',
    dialCode: '',
    phoneNumber: '---',
    text: '---',
    flag: '🌐',
    flagUrl: ''
  };

  if (!originalLink) {
    return fallback;
  }

  try {
    const parsed = new URL(originalLink);
    const host = parsed.hostname.toLowerCase();
    const pathValue = parsed.pathname.replace(/^\/+/, '');

    let phoneRaw = '';
    if (host.includes('wa.me')) {
      phoneRaw = pathValue;
    } else if (host.includes('whatsapp.com')) {
      phoneRaw = parsed.searchParams.get('phone') || '';
    }

    const normalizedPhoneDigits = String(phoneRaw || '').replace(/[^\d]/g, '');
    const normalizedPhone = normalizedPhoneDigits ? `+${normalizedPhoneDigits}` : '';

    const matchedDialCode = normalizedPhone
      ? [...countryPhoneOptions]
          .sort((left, right) => right.value.length - left.value.length)
          .find((option) => normalizedPhone.startsWith(option.value))
      : null;

    const dialDigits = String(matchedDialCode?.value || '').replace(/[^\d]/g, '');
    const localDigits =
      normalizedPhoneDigits && dialDigits && normalizedPhoneDigits.startsWith(dialDigits)
        ? normalizedPhoneDigits.slice(dialDigits.length)
        : normalizedPhoneDigits;

    const textValue = (parsed.searchParams.get('text') || '').trim();

    return {
      countryName: matchedDialCode?.name || '---',
      countryCode: matchedDialCode?.countryCode || '',
      dialCode: matchedDialCode?.value || '',
      phoneNumber: localDigits || '---',
      text: textValue || '---',
      flag: matchedDialCode?.flag || '🌐',
      flagUrl: matchedDialCode?.flagUrl || ''
    };
  } catch {
    return fallback;
  }
};

const buildWhatsappOriginalLink = ({ phone, text }) => {
  const phoneDigits = String(phone || '')
    .trim()
    .replace(/[^\d]/g, '');

  if (!phoneDigits) {
    throw new Error('Phone number is required.');
  }

  const trimmedText = String(text || '').trim();
  const base = `https://wa.me/${phoneDigits}`;
  if (!trimmedText) {
    return base;
  }
  return `${base}?text=${encodeURIComponent(trimmedText)}`;
};

const parseLinkDetailsByApp = (originalLink, appId) => {
  if (appId === 'whatsapp') {
    return parseWhatsappDetails(originalLink);
  }

  const fallback = {
    recipient: '---',
    text: '---'
  };

  if (!originalLink) {
    return fallback;
  }

  try {
    const parsed = new URL(originalLink);
    const recipient = String(parsed.pathname || '')
      .replace(/^\/+/, '')
      .trim()
      .replace(/^@/, '');

    if (appId === 'telegram') {
      return {
        recipient: recipient || '---',
        text: (parsed.searchParams.get('text') || '').trim() || '---'
      };
    }

    if (appId === 'messenger') {
      return {
        recipient: recipient || '---',
        text: (parsed.searchParams.get('ref') || '').trim() || '---'
      };
    }

    if (appId === 'instagram') {
      return {
        recipient: recipient || '---',
        text: '---'
      };
    }

    return {
      recipient: recipient || '---',
      text: '---'
    };
  } catch {
    return fallback;
  }
};

const buildOriginalLinkByApp = ({ appId, dialCode, phoneNumber, recipient, text }) => {
  if (appId === 'whatsapp') {
    const dialDigits = String(dialCode || '').replace(/[^\d]/g, '');
    const localDigits = String(phoneNumber || '').replace(/[^\d]/g, '');
    if (!dialDigits || !localDigits) {
      throw new Error('Please enter a valid phone number.');
    }
    return buildWhatsappOriginalLink({
      phone: `${dialDigits}${localDigits}`,
      text
    });
  }

  const app = getAppById(appId || '');
  const built = app?.buildLink ? app.buildLink(String(recipient || '').trim(), String(text || '').trim()) : '';
  if (!built) {
    throw new Error('Please enter required details.');
  }
  return built;
};

function Sidebar({ collapsed, currentUser, onOpenAuth, onOpenPersonalisation, onOpenDashboard, onLogout }) {
  const profileName = currentUser?.displayName || currentUser?.email || 'Profile';
  const avatarFallback = profileName.trim().charAt(0).toUpperCase();
  const [menuOpen, setMenuOpen] = useState(false);
  const profileAreaRef = useRef(null);

  useEffect(() => {
    if (!currentUser) {
      setMenuOpen(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const handleClickOutside = (event) => {
      if (profileAreaRef.current?.contains(event.target)) {
        return;
      }
      setMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-inner">
        {!collapsed && <p className="section-title">Link Generator</p>}

        <nav className="app-nav">
          {linkApps.map((app) => (
            <NavLink
              key={app.id}
              to={`/generator/${app.id}`}
              className="app-link"
              title={app.name}
              aria-label={app.name}
              data-tooltip={app.name}
            >
              <span className={`app-icon app-icon-${app.id}`}>
                {(() => {
                  const Icon = appIcons[app.id] ?? FiHome;
                  return <Icon />;
                })()}
              </span>
              {!collapsed && <span>{app.name}</span>}
            </NavLink>
          ))}
          <button
            type="button"
            className="app-link dashboard-link"
            onClick={onOpenDashboard}
            aria-label="My Dashboard"
            data-tooltip="My Dashboard"
          >
            <span className="app-icon app-icon-dashboard">
              <FiGrid />
            </span>
            {!collapsed && <span>My Dashboard</span>}
          </button>
        </nav>
      </div>

      <div className="sidebar-footer" ref={profileAreaRef}>
        <div className="sidebar-profile-divider" aria-hidden="true" />
        {currentUser ? (
          <div className="profile-anchor">
            {menuOpen && (
              <div className="profile-pop-card" role="menu" aria-label="Profile menu">
                <div className="profile-pop-header">
                  <span className="profile-pop-avatar-wrap">
                    {currentUser.photoURL ? (
                      <img
                        src={currentUser.photoURL}
                        alt={profileName}
                        className="profile-pop-avatar"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <span className="profile-pop-avatar-fallback">{avatarFallback || <FiUser />}</span>
                    )}
                  </span>
                  <div className="profile-pop-meta">
                    <strong>{currentUser.displayName || 'User Account'}</strong>
                    <small>{currentUser.email}</small>
                  </div>
                </div>

                <hr />

                <button
                  type="button"
                  className="profile-pop-action"
                  onClick={() => {
                    setMenuOpen(false);
                    onOpenPersonalisation();
                  }}
                >
                  Personalisation
                </button>

                <hr />

                <button
                  type="button"
                  className="profile-pop-action profile-pop-logout"
                  onClick={async () => {
                    setMenuOpen(false);
                    await onLogout();
                  }}
                >
                  Logout
                </button>
              </div>
            )}

            <button
              type="button"
              className="settings-trigger profile-trigger"
              onClick={() => setMenuOpen((previous) => !previous)}
              aria-label="Open profile"
              data-tooltip={profileName}
            >
              <span className="profile-avatar-wrap app-icon">
                {currentUser.photoURL ? (
                  <img
                    src={currentUser.photoURL}
                    alt={profileName}
                    className="profile-avatar"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span className="profile-avatar-fallback">{avatarFallback || <FiUser />}</span>
                )}
              </span>
              {!collapsed && (
                <span className="profile-meta">
                  <strong>{currentUser.displayName || 'User Account'}</strong>
                  <small>{currentUser.email}</small>
                </span>
              )}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="settings-trigger"
            onClick={onOpenAuth}
            aria-label="Login or Sign Up"
            data-tooltip="Login / Sign Up"
          >
            <span className="app-icon settings-icon">
              <FiUser />
            </span>
            {!collapsed && <span>Login / Sign Up</span>}
          </button>
        )}
      </div>
    </aside>
  );
}

function ThemeControls() {
  const { mode, palette, toggleMode, setColor, resetCurrentTheme } = useTheme();
  const tokens = useMemo(() => Object.keys(palette), [palette]);

  return (
    <section className="theme-controls">
      <div className="theme-controls-top">
        <button type="button" className="theme-toggle" onClick={toggleMode}>
          {mode === 'light' ? (
            <>
              <FiMoon /> Dark
            </>
          ) : (
            <>
              <FiSun /> Light
            </>
          )}
        </button>
        <button type="button" className="theme-reset" onClick={resetCurrentTheme}>
          Reset Colors
        </button>
      </div>
      <div className="color-grid">
        {tokens.map((token) => (
          <label key={token} className="color-field">
            <span>{token}</span>
            <input type="color" value={palette[token]} onChange={(event) => setColor(token, event.target.value)} />
          </label>
        ))}
      </div>
    </section>
  );
}

const mapAuthError = (error) => {
  if (error?.code === 'auth/user-disabled') {
    return 'Your account has been disabled temporarily. Submit a request via contact@photremium.com';
  }

  return error?.message || 'Authentication failed. Please try again.';
};

const isGmailAddress = (email) => /^[a-zA-Z0-9._%+-]+@gmail\.com$/i.test(email.trim());

function AuthModal({ open, onClose, currentUser, onAuthSuccess }) {
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [disabledAccountError, setDisabledAccountError] = useState(false);

  if (!open) {
    return null;
  }

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    setDisabledAccountError(false);

    const normalizedEmail = email.trim().toLowerCase();

    try {
      if (mode === 'signup') {
        if (!isGmailAddress(normalizedEmail)) {
          setErrorMessage('Please enter a valid Gmail address (example@gmail.com).');
          return;
        }

        const credentials = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
        if (name.trim()) {
          await updateProfile(credentials.user, { displayName: name.trim() });
        }
        await sendEmailVerification(credentials.user);
        onAuthSuccess('Account created successfuly');
      } else {
        const credentials = await signInWithEmailAndPassword(auth, normalizedEmail, password);

        if (!credentials.user.emailVerified) {
          await sendEmailVerification(credentials.user);
          await signOut(auth);
          setErrorMessage('Your email is not verified yet. We sent a verification email. Please verify and login again.');
          return;
        }

        onAuthSuccess('Log In successfully');
      }

      setPassword('');
    } catch (error) {
      setErrorMessage(mapAuthError(error));
      setDisabledAccountError(error?.code === 'auth/user-disabled');
    } finally {
      setLoading(false);
    }
  };

  const signInGoogle = async () => {
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    setDisabledAccountError(false);
    try {
      const credential = await signInWithPopup(auth, googleProvider);
      const isNewUser = Boolean(credential?.user?.metadata?.creationTime === credential?.user?.metadata?.lastSignInTime);
      onAuthSuccess(isNewUser ? 'Account created successfuly' : 'Log In successfully');
    } catch (error) {
      setErrorMessage(mapAuthError(error));
      setDisabledAccountError(error?.code === 'auth/user-disabled');
    } finally {
      setLoading(false);
    }
  };

  const requestPasswordReset = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    setDisabledAccountError(false);

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setErrorMessage('Enter your email first, then click Forgot Password.');
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, normalizedEmail);
      setSuccessMessage('Password reset email sent. Please check your inbox.');
    } catch (error) {
      setErrorMessage(mapAuthError(error));
      setDisabledAccountError(error?.code === 'auth/user-disabled');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-modal-backdrop" onClick={onClose}>
      <button type="button" className="auth-modal-close" onClick={onClose} aria-label="Close authentication modal">
        <FiX />
      </button>

      <div className="auth-modal-card" onClick={(event) => event.stopPropagation()}>
        <section className="auth-left">
          <h2>{currentUser ? 'Account' : mode === 'login' ? 'Login' : 'Sign Up'}</h2>

          {currentUser ? (
            <div className="auth-logged-in">
              <p>
                Signed in as <strong>{currentUser.displayName || currentUser.email}</strong>
              </p>
              <button
                type="button"
                onClick={async () => {
                  await signOut(auth);
                }}
              >
                <FiLogOut /> Logout
              </button>
            </div>
          ) : (
            <>
              <form className="auth-form" onSubmit={submit}>
                {mode === 'signup' && (
                  <label>
                    <span>Full Name</span>
                    <input value={name} onChange={(event) => setName(event.target.value)} placeholder="John Doe" />
                  </label>
                )}

                <label>
                  <span>Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    required
                  />
                </label>

                <label>
                  <span>Password</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </label>

                <button type="submit" disabled={loading}>
                  {loading ? 'Please wait...' : mode === 'login' ? 'Login' : 'Create Account'}
                </button>
              </form>

              <button type="button" className="google-auth-btn" onClick={signInGoogle} disabled={loading}>
                <span className="google-auth-icon" aria-hidden="true">
                  <FcGoogle />
                </span>
                Sign in with Google
              </button>

              <p className="auth-switch">
                {mode === 'login' ? 'New here?' : 'Already have an account?'}{' '}
                <button
                  type="button"
                  onClick={() => {
                    setMode((prev) => (prev === 'login' ? 'signup' : 'login'));
                    setErrorMessage('');
                    setSuccessMessage('');
                    setDisabledAccountError(false);
                  }}
                >
                  {mode === 'login' ? 'Sign Up' : 'Login'}
                </button>
              </p>

              {mode === 'login' && (
                <button type="button" className="auth-forgot-btn" onClick={requestPasswordReset} disabled={loading}>
                  Forgot Password?
                </button>
              )}
            </>
          )}

          {disabledAccountError ? (
            <div className="auth-disabled-card" role="alert" aria-live="polite">
              <h4>Account Disabled</h4>
              <p>
                Your account has been disabled temporarily. Submit a request via{' '}
                <a href="mailto:contact@photremium.com">contact@photremium.com</a>
              </p>
            </div>
          ) : (
            <>
              {errorMessage && <p className="auth-error">{errorMessage}</p>}
              {successMessage && <p className="auth-info">{successMessage}</p>}
            </>
          )}
        </section>

        <aside className="auth-right">
          <h3>Welcome to Link Generator</h3>
          <p>
            Create direct message links for WhatsApp, Telegram, Messenger, and Instagram in seconds with live
            preview and one-click sharing.
          </p>
          <ul>
            <li>Secure Firebase authentication</li>
            <li>Google sign-in enabled</li>
            <li>Access your saved workflow quickly</li>
          </ul>
        </aside>
      </div>
    </div>
  );
}

function HomePage() {
  return (
    <section className="content-page home-page">
      <div className="home-cards-grid">
        {linkApps.map((app) => {
          const Icon = appIcons[app.id] ?? FiHome;

          return (
            <NavLink key={app.id} to={`/generator/${app.id}`} className="home-app-card">
              <span className={`home-app-icon app-icon-${app.id}`}>
                <Icon />
              </span>
              <strong>{`Generate ${app.name} link`}</strong>
              <small>{app.description}</small>
            </NavLink>
          );
        })}
      </div>
    </section>
  );
}

function SettingsPage() {
  return (
    <section className="content-page">
      <ThemeControls />
    </section>
  );
}

function DashboardPage({ currentUser, onOpenAuth, onNotify }) {
  const [allLinks, setAllLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [appFilter, setAppFilter] = useState('all');
  const [clickFilter, setClickFilter] = useState('all');
  const [analyticsOpenFor, setAnalyticsOpenFor] = useState('');
  const [analyticsModalItem, setAnalyticsModalItem] = useState(null);
  const [analyticsRaw, setAnalyticsRaw] = useState(null);
  const [analyticsCache, setAnalyticsCache] = useState({});
  const [analyticsLoadingFor, setAnalyticsLoadingFor] = useState('');
  const [analyticsError, setAnalyticsError] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [linkOverrides, setLinkOverrides] = useState({});
  const [editingOriginalLink, setEditingOriginalLink] = useState(false);
  const [dialCodeDraft, setDialCodeDraft] = useState('');
  const [recipientDraft, setRecipientDraft] = useState('');
  const [phoneDraft, setPhoneDraft] = useState('');
  const [textDraft, setTextDraft] = useState('');
  const [infoCountryPickerOpen, setInfoCountryPickerOpen] = useState(false);
  const [infoCountrySearch, setInfoCountrySearch] = useState('');
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [pendingOriginalLink, setPendingOriginalLink] = useState('');
  const [generatedLinkCopied, setGeneratedLinkCopied] = useState(false);
  const [linkInfoError, setLinkInfoError] = useState('');
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState('clicks');
  const [activeRange, setActiveRange] = useState('24h');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [customDropdownOpen, setCustomDropdownOpen] = useState(false);
  const [chartMode, setChartMode] = useState('curve');
  const [hoveredChartIndex, setHoveredChartIndex] = useState(-1);
  const [analyticsFetchedAt, setAnalyticsFetchedAt] = useState(0);
  const [analyticsTimerNow, setAnalyticsTimerNow] = useState(Date.now());
  const [dashboardFetchedAt, setDashboardFetchedAt] = useState(0);
  const [dashboardTimerNow, setDashboardTimerNow] = useState(Date.now());
  const [worldMapPaths, setWorldMapPaths] = useState([]);
  const [mapTooltip, setMapTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    country: '',
    clicks: 0
  });
  const countryMapWrapRef = useRef(null);
  const generatedLinkCopyTimerRef = useRef(null);
  const analyticsStorageKey = currentUser?.uid ? `${ANALYTICS_STORAGE_KEY}:${currentUser.uid}` : '';
  const linkOverridesStorageKey = currentUser?.uid ? `${LINK_OVERRIDES_STORAGE_KEY}:${currentUser.uid}` : '';

  useEffect(() => {
    return () => {
      if (generatedLinkCopyTimerRef.current) {
        clearTimeout(generatedLinkCopyTimerRef.current);
      }
    };
  }, []);

  const loadDashboardLinks = useCallback(async () => {
    if (!currentUser) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await listCloudflareLinks({ user: currentUser, appId: 'all' });
      setAllLinks(Array.isArray(response?.links) ? response.links : []);
      const fetchedAt = Date.now();
      setDashboardFetchedAt(fetchedAt);
      setDashboardTimerNow(fetchedAt);
    } catch (requestError) {
      setError(requestError.message || 'Failed to load dashboard links.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setAllLinks([]);
      setAnalyticsCache({});
      setLinkOverrides({});
      setDashboardFetchedAt(0);
      return;
    }

    loadDashboardLinks();
  }, [currentUser, loadDashboardLinks]);

  useEffect(() => {
    if (!analyticsStorageKey) {
      return;
    }

    try {
      const raw = localStorage.getItem(analyticsStorageKey);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        setAnalyticsCache(parsed);
      }
    } catch {
      setAnalyticsCache({});
    }
  }, [analyticsStorageKey]);

  useEffect(() => {
    if (!analyticsStorageKey) {
      return;
    }
    try {
      localStorage.setItem(analyticsStorageKey, JSON.stringify(analyticsCache));
    } catch {
      // Ignore storage quota errors.
    }
  }, [analyticsStorageKey, analyticsCache]);

  useEffect(() => {
    if (!linkOverridesStorageKey) {
      return;
    }

    try {
      const raw = localStorage.getItem(linkOverridesStorageKey);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        setLinkOverrides(parsed);
      }
    } catch {
      setLinkOverrides({});
    }
  }, [linkOverridesStorageKey]);

  useEffect(() => {
    if (!linkOverridesStorageKey) {
      return;
    }
    try {
      localStorage.setItem(linkOverridesStorageKey, JSON.stringify(linkOverrides));
    } catch {
      // Ignore storage quota errors.
    }
  }, [linkOverridesStorageKey, linkOverrides]);

  const decoratedLinks = useMemo(
    () =>
      allLinks.map((link) => {
        const override = linkOverrides[link.shortCode];
        if (!override?.originalLink) {
          return link;
        }
        return {
          ...link,
          originalLink: override.originalLink
        };
      }),
    [allLinks, linkOverrides]
  );

  const informationDetails = useMemo(() => {
    const appId = analyticsModalItem?.appId;
    const shortCode = analyticsModalItem?.shortCode;
    const override = shortCode ? linkOverrides[shortCode] : null;
    const baseDetails = parseLinkDetailsByApp(analyticsModalItem?.originalLink, appId);

    if (!override || typeof override !== 'object') {
      return baseDetails;
    }

    if (appId === 'whatsapp') {
      const mergedDialCode = override.dialCode || baseDetails.dialCode || '';
      const matchedOption = mergedDialCode
        ? countryPhoneOptions.find((option) => option.value === mergedDialCode)
        : null;

      return {
        countryName: override.countryName || matchedOption?.name || baseDetails.countryName || '---',
        countryCode: override.countryCode || matchedOption?.countryCode || baseDetails.countryCode || '',
        dialCode: mergedDialCode,
        phoneNumber: override.phoneNumber || baseDetails.phoneNumber || '---',
        text: typeof override.text === 'string' ? override.text || '---' : baseDetails.text,
        flag: override.flag || matchedOption?.flag || baseDetails.flag || '🌐',
        flagUrl: override.flagUrl || matchedOption?.flagUrl || baseDetails.flagUrl || ''
      };
    }

    return {
      recipient: override.recipient || baseDetails.recipient || '---',
      text: typeof override.text === 'string' ? override.text || '---' : baseDetails.text || '---'
    };
  }, [analyticsModalItem, linkOverrides]);

  const selectedInfoCountryOption = useMemo(() => {
    if (!dialCodeDraft) {
      return null;
    }
    return countryPhoneOptions.find((option) => option.value === dialCodeDraft) || null;
  }, [dialCodeDraft]);

  const filteredInfoCountryOptions = useMemo(() => {
    const query = infoCountrySearch.trim().toLowerCase();
    if (!query) {
      return countryPhoneOptions;
    }
    return countryPhoneOptions.filter((option) => {
      return (
        option.name.toLowerCase().includes(query) ||
        option.value.includes(query) ||
        option.countryCode.toLowerCase().includes(query)
      );
    });
  }, [infoCountrySearch]);

  const hasInformationChanges = useMemo(() => {
    if (!editingOriginalLink) {
      return false;
    }

    const appId = analyticsModalItem?.appId;
    if (appId === 'whatsapp') {
      const initialDial = informationDetails.dialCode || '';
      const initialPhone = informationDetails.phoneNumber === '---' ? '' : informationDetails.phoneNumber;
      const initialText = informationDetails.text === '---' ? '' : informationDetails.text;
      return (
        initialDial !== dialCodeDraft ||
        initialPhone.trim() !== phoneDraft.trim() ||
        initialText.trim() !== textDraft.trim()
      );
    }

    const initialRecipient = informationDetails.recipient === '---' ? '' : informationDetails.recipient;
    const initialText = informationDetails.text === '---' ? '' : informationDetails.text;
    const supportsText = appId === 'telegram' || appId === 'messenger';

    if (supportsText) {
      return initialRecipient.trim() !== recipientDraft.trim() || initialText.trim() !== textDraft.trim();
    }

    return initialRecipient.trim() !== recipientDraft.trim();
  }, [
    analyticsModalItem,
    dialCodeDraft,
    editingOriginalLink,
    informationDetails,
    phoneDraft,
    recipientDraft,
    textDraft
  ]);

  const filteredLinks = useMemo(
    () =>
      decoratedLinks
        .filter((link) => (appFilter === 'all' ? true : link.appId === appFilter))
        .filter((link) => {
          const clicks = Number(link.clicks || 0);
          if (clickFilter === 'clicked') {
            return clicks > 0;
          }
          if (clickFilter === 'not-clicked') {
            return clicks === 0;
          }
          return true;
        }),
    [decoratedLinks, appFilter, clickFilter]
  );

  const drawBarWidth = (count, maxCount) => {
    if (!maxCount || maxCount <= 0) {
      return 0;
    }
    return Math.max(6, Math.round((Number(count || 0) / maxCount) * 100));
  };

  const sortCountRows = (countMap, key) =>
    Array.from(countMap.entries())
      .map(([label, count]) => ({ [key]: label, count }))
      .sort((left, right) => Number(right.count || 0) - Number(left.count || 0));

  const formatUpdatedSince = (fetchedAt, nowMs) => {
    if (!fetchedAt) {
      return '---';
    }

    const elapsedSeconds = Math.max(0, Math.floor((nowMs - fetchedAt) / 1000));
    if (elapsedSeconds < 10) {
      return 'Just now';
    }

    if (elapsedSeconds < 60) {
      const roundedTens = Math.floor(elapsedSeconds / 10) * 10;
      return `${roundedTens} seconds`;
    }

    const elapsedMinutes = Math.floor(elapsedSeconds / 60);
    return elapsedMinutes === 1 ? '1 min' : `${elapsedMinutes} mins`;
  };

  const updatedSinceLabel = useMemo(
    () => formatUpdatedSince(analyticsFetchedAt, analyticsTimerNow),
    [analyticsFetchedAt, analyticsTimerNow]
  );

  const dashboardUpdatedSinceLabel = useMemo(
    () => formatUpdatedSince(dashboardFetchedAt, dashboardTimerNow),
    [dashboardFetchedAt, dashboardTimerNow]
  );

  const getUpdatedDotStatus = (elapsedSeconds) => {
    if (elapsedSeconds <= 30) {
      return 'green';
    }
    if (elapsedSeconds > 30 && elapsedSeconds < 120) {
      return 'yellow';
    }
    return 'red';
  };

  const updatedElapsedSeconds = useMemo(() => {
    if (!analyticsFetchedAt) {
      return 0;
    }
    return Math.max(0, Math.floor((analyticsTimerNow - analyticsFetchedAt) / 1000));
  }, [analyticsFetchedAt, analyticsTimerNow]);

  const updatedDotStatus = useMemo(() => {
    return getUpdatedDotStatus(updatedElapsedSeconds);
  }, [updatedElapsedSeconds]);

  const dashboardElapsedSeconds = useMemo(() => {
    if (!dashboardFetchedAt) {
      return 0;
    }
    return Math.max(0, Math.floor((dashboardTimerNow - dashboardFetchedAt) / 1000));
  }, [dashboardFetchedAt, dashboardTimerNow]);

  const dashboardDotStatus = useMemo(() => getUpdatedDotStatus(dashboardElapsedSeconds), [dashboardElapsedSeconds]);

  const withAgoSuffix = (value) => {
    if (value === 'Just now' || value === '---') {
      return value;
    }
    return `${value} ago`;
  };

  useEffect(() => {
    if (!analyticsOpenFor || !analyticsFetchedAt) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setAnalyticsTimerNow(Date.now());
    }, 10000);

    return () => {
      clearInterval(intervalId);
    };
  }, [analyticsOpenFor, analyticsFetchedAt]);

  useEffect(() => {
    if (!dashboardFetchedAt) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setDashboardTimerNow(Date.now());
    }, 10000);

    return () => {
      clearInterval(intervalId);
    };
  }, [dashboardFetchedAt]);

  useEffect(() => {
    if (!analyticsOpenFor || activeAnalyticsTab !== 'country' || worldMapPaths.length > 0) {
      return undefined;
    }

    let mounted = true;

    const loadWorldGeoJson = async () => {
      try {
        const response = await fetch(WORLD_GEOJSON_URL);
        if (!response.ok) {
          return;
        }
        const payload = await response.json();
        if (mounted && payload?.features) {
          const projection = geoNaturalEarth1().fitSize([WORLD_MAP_WIDTH, WORLD_MAP_HEIGHT], payload);
          const pathBuilder = geoPath(projection);
          const precomputedPaths = payload.features
            .map((feature, index) => {
              const code = String(
                feature?.properties?.['ISO3166-1-Alpha-2'] || feature?.properties?.ISO_A2 || ''
              ).toUpperCase();
              const name = String(feature?.properties?.name || 'Unknown');
              const d = pathBuilder(feature);
              if (!d) {
                return null;
              }
              return {
                id: `${code || 'xx'}-${name}-${index}`,
                code,
                name,
                d
              };
            })
            .filter(Boolean);

          setWorldMapPaths(precomputedPaths);
        }
      } catch {
        // Keep country cards functional even if map loading fails.
      }
    };

    loadWorldGeoJson();

    return () => {
      mounted = false;
    };
  }, [analyticsOpenFor, activeAnalyticsTab, worldMapPaths.length]);

  const getInRangeWindow = (rangeType, allEvents) => {
    const now = Date.now();
    const fallbackStart = allEvents.length > 0 ? allEvents[0].timestamp : now;
    const fallbackEnd = allEvents.length > 0 ? allEvents[allEvents.length - 1].timestamp : now;

    if (rangeType === 'all') {
      return { startMs: fallbackStart, endMs: now };
    }

    if (rangeType === '15m') {
      return { startMs: now - 15 * 60 * 1000, endMs: now };
    }
    if (rangeType === '1h') {
      return { startMs: now - 60 * 60 * 1000, endMs: now };
    }
    if (rangeType === '24h') {
      return { startMs: now - 24 * 60 * 60 * 1000, endMs: now };
    }
    if (rangeType === 'custom') {
      return {
        startMs: new Date(customStart).getTime(),
        endMs: new Date(customEnd).getTime()
      };
    }
    return { startMs: fallbackStart, endMs: fallbackEnd };
  };

  const applyAnalyticsFilter = (rawAnalytics, rangeType) => {
    if (!rawAnalytics) {
      setAnalytics(null);
      return;
    }

    setAnalyticsError('');

    const parseEventTimestamp = (value) => {
      const raw = String(value || '').trim();
      if (!raw) {
        return NaN;
      }

      const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
      const withZone = /z$/i.test(normalized) ? normalized : `${normalized}Z`;
      return new Date(withZone).getTime();
    };

    const clickEvents = (Array.isArray(rawAnalytics.clickEvents) ? rawAnalytics.clickEvents : [])
      .map((entry) => {
        const stamp = parseEventTimestamp(entry?.createdAt);
        if (Number.isNaN(stamp)) {
          return null;
        }
        return { createdAt: new Date(stamp).toISOString(), timestamp: stamp };
      })
      .filter(Boolean)
      .sort((left, right) => left.timestamp - right.timestamp);

    const traceEvents = (Array.isArray(rawAnalytics.traceEvents) ? rawAnalytics.traceEvents : [])
      .map((entry) => {
        const stamp = parseEventTimestamp(entry?.createdAt);
        if (Number.isNaN(stamp)) {
          return null;
        }
        return {
          createdAt: new Date(stamp).toISOString(),
          timestamp: stamp,
          country: entry?.country || 'unknown',
          browser: entry?.browser || 'unknown',
          os: entry?.os || 'unknown',
          device: entry?.device || 'unknown'
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.timestamp - right.timestamp);

    const eventSource = clickEvents.length > 0 ? clickEvents : traceEvents;
    const window = getInRangeWindow(rangeType, eventSource);
    const startMs = Number(window.startMs);
    const endMs = Number(window.endMs);
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) {
      setAnalyticsError('Invalid selected time window.');
      return;
    }

    const inRangeClicks = clickEvents.filter((entry) => entry.timestamp >= startMs && entry.timestamp <= endMs);
    const inRangeTraces = traceEvents.filter((entry) => entry.timestamp >= startMs && entry.timestamp <= endMs);

    const countryMap = new Map();
    const browserMap = new Map();
    const osMap = new Map();
    const deviceMap = new Map();

    inRangeTraces.forEach((entry) => {
      countryMap.set(entry.country, (countryMap.get(entry.country) || 0) + 1);
      browserMap.set(entry.browser, (browserMap.get(entry.browser) || 0) + 1);
      osMap.set(entry.os, (osMap.get(entry.os) || 0) + 1);
      deviceMap.set(entry.device, (deviceMap.get(entry.device) || 0) + 1);
    });

    setAnalytics({
      shortCode: rawAnalytics.shortCode,
      range: rangeType,
      start: new Date(startMs).toISOString(),
      end: new Date(endMs).toISOString(),
      clicks: inRangeClicks.length,
      clicksAllTime: Number(rawAnalytics.clicksAllTime || clickEvents.length),
      clickEvents: inRangeClicks,
      countries: sortCountRows(countryMap, 'country'),
      browsers: sortCountRows(browserMap, 'browser'),
      os: sortCountRows(osMap, 'os'),
      devices: sortCountRows(deviceMap, 'device')
    });
  };

  const fetchAnalytics = async (shortCode, options = {}) => {
    if (!shortCode) {
      return;
    }

    const forceRefresh = Boolean(options.forceRefresh);
    const requestedRange = options.rangeType || '24h';
    const appliedRange = requestedRange === 'custom' && (!customStart || !customEnd) ? '24h' : requestedRange;

    if (!forceRefresh && analyticsCache[shortCode]) {
      const cached = analyticsCache[shortCode];
      const cachedPayload = cached?.payload || cached;
      const cachedFetchedAt = Number(cached?.fetchedAt || Date.now());
      setAnalyticsRaw(cachedPayload);
      setAnalyticsFetchedAt(cachedFetchedAt);
      setAnalyticsTimerNow(Date.now());
      applyAnalyticsFilter(cachedPayload, appliedRange);
      return;
    }

    setAnalyticsLoadingFor(shortCode);
    setAnalyticsError('');

    try {
      const response = await getCloudflareLinkAnalytics({
        user: currentUser,
        shortCode,
        range: 'all'
      });
      const payload = response || null;
      const fetchedAt = Date.now();
      setAnalyticsRaw(payload);
      setAnalyticsFetchedAt(fetchedAt);
      setAnalyticsTimerNow(fetchedAt);
      setAnalyticsCache((prev) => ({ ...prev, [shortCode]: { payload, fetchedAt } }));
      applyAnalyticsFilter(payload, appliedRange);
    } catch (requestError) {
      setAnalyticsRaw(null);
      setAnalytics(null);
      setAnalyticsError(requestError.message || 'Failed to load analytics.');
    } finally {
      setAnalyticsLoadingFor('');
    }
  };

  const openAnalytics = async (item) => {
    const shortCode = item?.shortCode;
    if (!shortCode) {
      return;
    }

    setAnalyticsOpenFor(shortCode);
    setAnalyticsModalItem(item);
    setActiveAnalyticsTab('clicks');
    setActiveRange('24h');
    setCustomStart('');
    setCustomEnd('');
    setCustomDropdownOpen(false);
    setEditingOriginalLink(false);
    const parsedDetails = parseLinkDetailsByApp(item?.originalLink, item?.appId);
    setDialCodeDraft(parsedDetails.dialCode || '');
    setPhoneDraft(parsedDetails.phoneNumber === '---' ? '' : parsedDetails.phoneNumber || '');
    setRecipientDraft(parsedDetails.recipient === '---' ? '' : parsedDetails.recipient || '');
    setTextDraft(parsedDetails.text === '---' ? '' : parsedDetails.text || '');
    setInfoCountryPickerOpen(false);
    setInfoCountrySearch('');
    setConfirmSaveOpen(false);
    setPendingOriginalLink('');
    setGeneratedLinkCopied(false);
    setLinkInfoError('');
    setAnalyticsError('');
    setAnalyticsRaw(null);
    setAnalytics(null);
    setAnalyticsFetchedAt(0);
    setAnalyticsTimerNow(Date.now());
    await fetchAnalytics(shortCode, { rangeType: '24h' });
  };

  const closeAnalyticsModal = () => {
    setAnalyticsOpenFor('');
    setAnalyticsModalItem(null);
    setAnalyticsLoadingFor('');
    setAnalyticsError('');
    setAnalyticsRaw(null);
    setAnalytics(null);
    setAnalyticsFetchedAt(0);
    setCustomDropdownOpen(false);
    setEditingOriginalLink(false);
    setDialCodeDraft('');
    setPhoneDraft('');
    setRecipientDraft('');
    setTextDraft('');
    setInfoCountryPickerOpen(false);
    setInfoCountrySearch('');
    setConfirmSaveOpen(false);
    setPendingOriginalLink('');
    setGeneratedLinkCopied(false);
    setLinkInfoError('');
  };

  const refreshAnalytics = async () => {
    if (!analyticsOpenFor) {
      return;
    }
    await fetchAnalytics(analyticsOpenFor, { forceRefresh: true, rangeType: activeRange });
  };

  const openCountryTab = async () => {
    setActiveAnalyticsTab('country');
    setMapTooltip({ visible: false, x: 0, y: 0, country: '', clicks: 0 });
    if (!analyticsOpenFor) {
      return;
    }
    await fetchAnalytics(analyticsOpenFor, { rangeType: activeRange });
  };

  const updateMapTooltip = (event, country, clicks) => {
    const containerRect = countryMapWrapRef.current?.getBoundingClientRect();
    if (!containerRect) {
      return;
    }

    const nextX = event.clientX - containerRect.left;
    const nextY = event.clientY - containerRect.top;

    setMapTooltip((previous) => {
      if (
        previous.visible &&
        previous.x === nextX &&
        previous.y === nextY &&
        previous.country === country &&
        previous.clicks === clicks
      ) {
        return previous;
      }

      return {
        visible: true,
        x: nextX,
        y: nextY,
        country,
        clicks
      };
    });
  };

  const refreshDashboard = async () => {
    await loadDashboardLinks();
  };

  const applyRange = async (rangeType) => {
    if (!analyticsOpenFor) {
      return;
    }
    setActiveRange(rangeType);
    if (rangeType !== 'custom') {
      setCustomDropdownOpen(false);
    }
    applyAnalyticsFilter(analyticsRaw, rangeType);
  };

  const applyCustomRange = async () => {
    if (!customStart || !customEnd) {
      setAnalyticsError('Select both custom start and end date/time.');
      return;
    }

    const startMs = new Date(customStart).getTime();
    const endMs = new Date(customEnd).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
      setAnalyticsError('Custom range has invalid date/time values.');
      return;
    }
    if (endMs <= startMs) {
      setAnalyticsError('End time must be after start time.');
      return;
    }
    if (endMs - startMs < 60 * 1000) {
      setAnalyticsError('Custom range must be at least 1 minute.');
      return;
    }

    setCustomDropdownOpen(false);
    setActiveRange('custom');
    applyAnalyticsFilter(analyticsRaw, 'custom');
  };

  const startEditOriginalLink = () => {
    setDialCodeDraft(informationDetails.dialCode || '');
    setPhoneDraft(informationDetails.phoneNumber === '---' ? '' : informationDetails.phoneNumber || '');
    setRecipientDraft(informationDetails.recipient === '---' ? '' : informationDetails.recipient || '');
    setTextDraft(informationDetails.text === '---' ? '' : informationDetails.text || '');
    setInfoCountryPickerOpen(false);
    setInfoCountrySearch('');
    setEditingOriginalLink(true);
    setLinkInfoError('');
  };

  const cancelEditOriginalLink = () => {
    setDialCodeDraft(informationDetails.dialCode || '');
    setPhoneDraft(informationDetails.phoneNumber === '---' ? '' : informationDetails.phoneNumber || '');
    setRecipientDraft(informationDetails.recipient === '---' ? '' : informationDetails.recipient || '');
    setTextDraft(informationDetails.text === '---' ? '' : informationDetails.text || '');
    setEditingOriginalLink(false);
    setLinkInfoError('');
    setInfoCountryPickerOpen(false);
    setInfoCountrySearch('');
    setConfirmSaveOpen(false);
    setPendingOriginalLink('');
  };

  const saveOriginalLinkChange = () => {
    const shortCode = analyticsModalItem?.shortCode;
    if (!shortCode) {
      return;
    }

    if (!hasInformationChanges) {
      return;
    }

    try {
      const normalized = buildOriginalLinkByApp({
        appId: analyticsModalItem?.appId,
        dialCode: dialCodeDraft,
        phoneNumber: phoneDraft,
        recipient: recipientDraft,
        text: textDraft
      });
      setPendingOriginalLink(normalized);
      setConfirmSaveOpen(true);
      setLinkInfoError('');
    } catch (builderError) {
      setLinkInfoError(builderError?.message || 'Please enter valid details.');
    }
  };

  const confirmSaveOriginalLinkChange = () => {
    const shortCode = analyticsModalItem?.shortCode;
    if (!shortCode || !pendingOriginalLink) {
      return;
    }

    const appId = analyticsModalItem?.appId;
    const nextOverride = {
      originalLink: pendingOriginalLink,
      updatedAt: new Date().toISOString()
    };

    if (appId === 'whatsapp') {
      nextOverride.dialCode = dialCodeDraft || '';
      nextOverride.countryCode = selectedInfoCountryOption?.countryCode || '';
      nextOverride.countryName = selectedInfoCountryOption?.name || '---';
      nextOverride.flag = selectedInfoCountryOption?.flag || '🌐';
      nextOverride.flagUrl = selectedInfoCountryOption?.flagUrl || '';
      nextOverride.phoneNumber = phoneDraft.trim() || '---';
      nextOverride.text = textDraft.trim() || '---';
    } else {
      nextOverride.recipient = recipientDraft.trim() || '---';
      if (appId === 'telegram' || appId === 'messenger') {
        nextOverride.text = textDraft.trim() || '---';
      }
    }

    setLinkOverrides((previous) => ({
      ...previous,
      [shortCode]: nextOverride
    }));

    setAnalyticsModalItem((previous) => {
      if (!previous) {
        return previous;
      }
      return {
        ...previous,
        originalLink: pendingOriginalLink
      };
    });

    setEditingOriginalLink(false);
    setInfoCountryPickerOpen(false);
    setInfoCountrySearch('');
    setConfirmSaveOpen(false);
    setPendingOriginalLink('');
    setLinkInfoError('');
    if (typeof onNotify === 'function') {
      onNotify('Link updated successfully.');
    }
  };

  const copyGeneratedLink = async () => {
    const link = analyticsModalItem?.shortUrl;
    if (!link) {
      return;
    }

    try {
      await navigator.clipboard.writeText(link);
      setGeneratedLinkCopied(true);
      if (generatedLinkCopyTimerRef.current) {
        clearTimeout(generatedLinkCopyTimerRef.current);
      }
      generatedLinkCopyTimerRef.current = setTimeout(() => {
        setGeneratedLinkCopied(false);
        generatedLinkCopyTimerRef.current = null;
      }, 1300);
      if (typeof onNotify === 'function') {
        onNotify('Link copied');
      }
    } catch {
      setLinkInfoError('Unable to copy generated link.');
    }
  };

  const toggleCustomDropdown = () => {
    setActiveRange('custom');
    setCustomDropdownOpen((prev) => !prev);
  };

  const renderBreakdown = (title, key, rows) => (
    <div className="dashboard-analytics-group" key={key}>
      <h4>{title}</h4>
      {rows.length === 0 ? (
        <p>No data yet.</p>
      ) : (
        <ul className="dashboard-graph-list">
          {(() => {
            const maxCount = Math.max(...rows.map((row) => Number(row.count || 0)));
            return rows.map((row) => (
              <li key={`${key}-${row[key] || 'unknown'}`}>
                <div className="dashboard-graph-row-top">
                  <span>{row[key] || 'unknown'}</span>
                  <strong>{Number(row.count || 0)}</strong>
                </div>
                <div className="dashboard-graph-track">
                  <div className="dashboard-graph-fill" style={{ width: `${drawBarWidth(row.count, maxCount)}%` }} />
                </div>
              </li>
            ));
          })()}
        </ul>
      )}
    </div>
  );

  const renderCountryBreakdown = (rows) => {
    if (rows.length === 0) {
      return (
        <div className="dashboard-analytics-group">
          <h4>Country Distribution</h4>
          <p>No country data yet.</p>
        </div>
      );
    }

    const maxCount = Math.max(...rows.map((row) => Number(row.count || 0)));
    const countryCountsByCode = rows.reduce((accumulator, row) => {
      const code = String(row.country || '')
        .trim()
        .toUpperCase();
      if (!/^[A-Z]{2}$/.test(code)) {
        return accumulator;
      }
      accumulator.set(code, Number(row.count || 0));
      return accumulator;
    }, new Map());

    return (
      <div className="dashboard-country-section">
        {worldMapPaths.length > 0 && (
          <div className="dashboard-country-map-card">
            <div className="dashboard-country-map-wrap" ref={countryMapWrapRef}>
              <svg viewBox={`0 0 ${WORLD_MAP_WIDTH} ${WORLD_MAP_HEIGHT}`} className="dashboard-country-map" role="img" aria-label="Country click intensity world map">
                {worldMapPaths.map((countryPath) => {
                  const countryCode = countryPath.code;
                  const clicks = countryCountsByCode.get(countryCode) || 0;
                  const intensity = maxCount > 0 ? clicks / maxCount : 0;
                  const accentPercent = Math.max(24, Math.round(24 + intensity * 76));
                  const fillColor =
                    clicks > 0
                      ? `color-mix(in srgb, var(--accent) ${accentPercent}%, var(--bg-shell))`
                      : 'color-mix(in srgb, var(--border) 32%, var(--bg-shell))';

                  return (
                    <path
                      key={`map-${countryPath.id}`}
                      d={countryPath.d}
                      className={`dashboard-country-map-path ${clicks > 0 ? 'has-clicks' : ''}`}
                      fill={fillColor}
                      onMouseEnter={(event) => updateMapTooltip(event, countryPath.name, clicks)}
                      onMouseLeave={() => setMapTooltip({ visible: false, x: 0, y: 0, country: '', clicks: 0 })}
                    />
                  );
                })}
              </svg>
              {mapTooltip.visible && (
                <div
                  className="dashboard-country-map-tooltip"
                  style={{
                    left: `${mapTooltip.x + 10}px`,
                    top: `${mapTooltip.y + 10}px`
                  }}
                >
                  <strong>{mapTooltip.country || 'Unknown'}</strong>
                  <span>{`${mapTooltip.clicks} ${mapTooltip.clicks === 1 ? 'click' : 'clicks'}`}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="dashboard-country-grid">
          {rows.map((row) => {
            const country = getCountryMeta(row.country);
            return (
              <article key={`country-${row.country || 'unknown'}`} className="dashboard-country-card">
                <div className="dashboard-country-top">
                  <span className="dashboard-country-label">
                    {country.flagUrl ? (
                      <img src={country.flagUrl} alt={country.name} className="dashboard-country-flag" loading="lazy" />
                    ) : (
                      <span>{country.flag}</span>
                    )}
                    <span>{country.name}</span>
                  </span>
                  <strong>{Number(row.count || 0)}</strong>
                </div>
                <div className="dashboard-graph-track">
                  <div className="dashboard-graph-fill" style={{ width: `${drawBarWidth(row.count, maxCount)}%` }} />
                </div>
              </article>
            );
          })}
        </div>
      </div>
    );
  };

  const renderClicksGraph = () => {
    const parseClickDate = (value) => {
      const raw = String(value || '').trim();
      if (!raw) {
        return null;
      }
      const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
      const date = new Date(normalized.endsWith('Z') ? normalized : `${normalized}Z`);
      return Number.isNaN(date.getTime()) ? null : date;
    };

    const events = (Array.isArray(analytics?.clickEvents) ? analytics.clickEvents : [])
      .map((row) => parseClickDate(row?.createdAt))
      .filter(Boolean)
      .sort((left, right) => left.getTime() - right.getTime());

    if (events.length === 0) {
      return <p>No click activity in selected range.</p>;
    }

    const startDateCandidate = analytics?.start ? new Date(analytics.start) : null;
    const endDateCandidate = analytics?.end ? new Date(analytics.end) : null;
    const fallbackStart = events[0];
    const fallbackEnd = events[events.length - 1];

    const rangeStart =
      startDateCandidate && !Number.isNaN(startDateCandidate.getTime())
        ? startDateCandidate
        : fallbackStart;
    const rangeEnd =
      endDateCandidate && !Number.isNaN(endDateCandidate.getTime())
        ? endDateCandidate
        : fallbackEnd;

    const startMs = rangeStart.getTime();
    let endMs = rangeEnd.getTime();
    if (endMs <= startMs) {
      endMs = startMs + 60 * 1000;
    }

    const oneMinuteMs = 60 * 1000;
    const totalMs = endMs - startMs;
    const maxAllowedSegments = Math.max(1, Math.floor(totalMs / oneMinuteMs));
    const desiredSegments = activeRange === 'all' ? 6 : 5;
    const segmentCount = Math.min(desiredSegments, maxAllowedSegments);
    const boundaryCount = Math.min(6, segmentCount + 1);
    const mainSegmentCount = Math.max(1, boundaryCount - 1);
    const segmentDurationMs = Math.max(oneMinuteMs, totalMs / mainSegmentCount);
    const subpartsPerSegment = 3;
    const subSegmentCount = mainSegmentCount * subpartsPerSegment;
    const subSegmentDurationMs = totalMs / subSegmentCount;

    const subSegments = Array.from({ length: subSegmentCount }, (_, index) => {
      const segmentStartMs = startMs + subSegmentDurationMs * index;
      const segmentEndMs = index === subSegmentCount - 1 ? endMs : startMs + subSegmentDurationMs * (index + 1);
      return {
        index,
        startMs: segmentStartMs,
        endMs: segmentEndMs,
        count: 0
      };
    });

    subSegments.forEach((segment, segmentIndex) => {
      const isLastSegment = segmentIndex === subSegments.length - 1;
      segment.count = events.filter((date) => {
        const time = date.getTime();
        if (isLastSegment) {
          return time >= segment.startMs && time <= segment.endMs;
        }
        return time >= segment.startMs && time < segment.endMs;
      }).length;
    });

    const chartHeight = 290;
    const chartWidth = 780;
    const padding = { top: 20, right: 24, bottom: 54, left: 54 };
    const plotWidth = chartWidth - padding.left - padding.right;
    const plotHeight = chartHeight - padding.top - padding.bottom;
    const maxCount = Math.max(...subSegments.map((row) => row.count), 1);

    const yStepCount = 5;
    const baseStep = maxCount <= yStepCount ? 1 : Math.ceil(maxCount / yStepCount);
    const yScaleMax = baseStep * yStepCount;
    const yPartitionValues = Array.from({ length: yStepCount + 1 }, (_, index) => index * baseStep);

    const boundaryTimes = Array.from({ length: boundaryCount }, (_, index) => {
      if (index === boundaryCount - 1) {
        return endMs;
      }
      return startMs + segmentDurationMs * index;
    });

    const axisPoints = boundaryTimes.map((time, index) => {
      const x =
        padding.left +
        (boundaryCount === 1 ? plotWidth / 2 : (index / (boundaryCount - 1)) * plotWidth);
      const segmentIndex = Math.min(subSegmentCount - 1, Math.max(0, index === 0 ? 0 : index * subpartsPerSegment - 1));
      const count = activeRange === 'all' && index === 0 ? 0 : subSegments[segmentIndex]?.count || 0;
      return { x, count, labelDate: new Date(time) };
    });

    const subBoundaryCount = subSegmentCount + 1;
    const subBoundaryTimes = Array.from({ length: subBoundaryCount }, (_, index) => {
      if (index === subBoundaryCount - 1) {
        return endMs;
      }
      return startMs + subSegmentDurationMs * index;
    });

    const points = subBoundaryTimes.map((time, index) => {
      const x =
        padding.left +
        (subBoundaryCount === 1 ? plotWidth / 2 : (index / (subBoundaryCount - 1)) * plotWidth);
      const segmentIndex = Math.min(subSegmentCount - 1, Math.max(0, index === 0 ? 0 : index - 1));
      const count = activeRange === 'all' && index === 0 ? 0 : subSegments[segmentIndex]?.count || 0;
      const y = padding.top + plotHeight - (count / yScaleMax) * plotHeight;
      return { x, y, count, labelDate: new Date(time) };
    });

    const linePath = points.reduce((acc, point, index) => {
      if (index === 0) {
        return `M ${point.x} ${point.y}`;
      }
      const previous = points[index - 1];
      const controlX = (previous.x + point.x) / 2;
      return `${acc} C ${controlX} ${previous.y}, ${controlX} ${point.y}, ${point.x} ${point.y}`;
    }, '');

    const cornerLinePath = points.reduce((acc, point, index) => {
      if (index === 0) {
        return `M ${point.x} ${point.y}`;
      }
      return `${acc} H ${point.x} V ${point.y}`;
    }, '');

    const triangularLinePath = points.reduce((acc, point, index) => {
      if (index === 0) {
        return `M ${point.x} ${point.y}`;
      }
      return `${acc} L ${point.x} ${point.y}`;
    }, '');

    const areaPath = `${linePath} L ${points[points.length - 1].x} ${padding.top + plotHeight} L ${points[0].x} ${padding.top + plotHeight} Z`;
    const cornerAreaPath = `${cornerLinePath} L ${points[points.length - 1].x} ${padding.top + plotHeight} L ${points[0].x} ${padding.top + plotHeight} Z`;
    const triangularAreaPath = `${triangularLinePath} L ${points[points.length - 1].x} ${padding.top + plotHeight} L ${points[0].x} ${padding.top + plotHeight} Z`;

    const barSlotWidth = plotWidth / Math.max(subSegmentCount, 1);
    const barWidth = Math.max(8, Math.min(22, barSlotWidth * 0.78));
    const createTopRoundedBarPath = (bar) => {
      const height = Math.max(1, bar.height);
      const radius = Math.min(4, bar.width / 2, height);
      const x = bar.x;
      const y = bar.y;
      const width = bar.width;
      const bottom = y + height;
      if (radius <= 0) {
        return `M ${x} ${bottom} L ${x + width} ${bottom} L ${x + width} ${y} L ${x} ${y} Z`;
      }
      return [
        `M ${x} ${bottom}`,
        `L ${x} ${y + radius}`,
        `Q ${x} ${y} ${x + radius} ${y}`,
        `L ${x + width - radius} ${y}`,
        `Q ${x + width} ${y} ${x + width} ${y + radius}`,
        `L ${x + width} ${bottom}`,
        'Z'
      ].join(' ');
    };

    const bars = subSegments.map((segment, index) => {
      const height = (segment.count / yScaleMax) * plotHeight;
      const boundaryPointX = padding.left + (index + 1) * barSlotWidth;
      return {
        index,
        x: boundaryPointX - barWidth / 2,
        y: padding.top + plotHeight - height,
        width: barWidth,
        height,
        count: segment.count,
        at: new Date(segment.endMs)
      };
    });

    const hoverZones = subSegments.map((segment, index) => ({
      index,
      x: padding.left + index * barSlotWidth,
      width: Math.max(2, barSlotWidth),
      startMs: segment.startMs,
      endMs: segment.endMs,
      count: segment.count
    }));

    const hoveredSegment = hoveredChartIndex >= 0 ? subSegments[hoveredChartIndex] || null : null;
    const hoveredPoint = hoveredChartIndex >= 0 ? points[Math.min(points.length - 1, hoveredChartIndex + 1)] || null : null;
    const hoveredBar = hoveredChartIndex >= 0 ? bars[hoveredChartIndex] || null : null;

    const formatHoverTime = (date) =>
      date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit'
      });

    const hoverCountLabel = hoveredSegment
      ? `${hoveredSegment.count} ${hoveredSegment.count === 1 ? 'click' : 'clicks'}`
      : '';
    const hoverRangeLabel = hoveredSegment
      ? `from ${formatHoverTime(new Date(hoveredSegment.startMs))} to ${formatHoverTime(new Date(hoveredSegment.endMs))}`
      : '';
    const hoverDateLabel = hoveredSegment ? `Date: ${new Date(hoveredSegment.endMs).toLocaleDateString('en-US')}` : '';

    const tooltipWidth = 214;
    const tooltipHeight = 66;
    const tooltipX = hoveredPoint
      ? Math.min(
          chartWidth - padding.right - tooltipWidth,
          Math.max(padding.left, hoveredPoint.x - tooltipWidth / 2)
        )
      : padding.left;
    const tooltipY = padding.top + 8;

    const formatSeriesLabel = (date) =>
      date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });

    return (
      <div className="dashboard-click-chart-card">
        <div className="dashboard-click-chart-wrap">
          <svg
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            className="dashboard-click-chart"
            role="img"
            aria-label="Clicks trend chart"
            onMouseLeave={() => setHoveredChartIndex(-1)}
          >
            {[0, ...yPartitionValues].map((value) => {
              const y = padding.top + plotHeight - (value / yScaleMax) * plotHeight;
              return (
                <g key={`y-grid-${value}`}>
                  <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} className="chart-grid-line" />
                  <text x={padding.left - 8} y={y + 4} textAnchor="end" className="chart-axis-label">
                    {value}
                  </text>
                </g>
              );
            })}

            {axisPoints.map((point, index) => (
              <line
                key={`x-grid-${index}`}
                x1={point.x}
                y1={padding.top}
                x2={point.x}
                y2={padding.top + plotHeight}
                className="chart-grid-line chart-grid-line-vertical"
              />
            ))}

            <g className="chart-mode-stage" key={chartMode}>
              {chartMode === 'curve' && (
                <>
                  <path d={areaPath} className="chart-area-fill chart-area-fill-animate" />
                  <path d={linePath} className="chart-line chart-line-animate" pathLength="1" />
                  {points.map((point, index) => (
                    <g key={`curve-${point.labelDate.toISOString()}-${point.x}`}>
                      <circle
                        cx={point.x}
                        cy={point.y}
                        r="4"
                        className="chart-point chart-point-animate"
                        style={{ animationDelay: `${index * 45}ms` }}
                      />
                      <title>{`${point.labelDate.toISOString()}: ${point.count}`}</title>
                    </g>
                  ))}
                </>
              )}

              {chartMode === 'corner' && (
                <>
                  <path d={cornerAreaPath} className="chart-area-fill chart-area-fill-animate" />
                  <path d={cornerLinePath} className="chart-line chart-line-corner chart-line-animate" pathLength="1" />
                  {points.map((point, index) => (
                    <g key={`corner-${point.labelDate.toISOString()}-${point.x}`}>
                      <circle
                        cx={point.x}
                        cy={point.y}
                        r="4"
                        className="chart-point chart-point-animate"
                        style={{ animationDelay: `${index * 45}ms` }}
                      />
                      <title>{`${point.labelDate.toISOString()}: ${point.count}`}</title>
                    </g>
                  ))}
                </>
              )}

              {chartMode === 'bars' && (
                <>
                  {bars.map((bar, index) => (
                    <g key={`bar-${index}-${bar.at.toISOString()}`}>
                      <path
                        d={createTopRoundedBarPath(bar)}
                        className={`chart-bar chart-bar-animate ${hoveredBar && hoveredBar.index === index ? 'active' : ''}`}
                        style={{ animationDelay: `${index * 18}ms` }}
                      />
                      <title>{`${bar.at.toISOString()}: ${bar.count}`}</title>
                    </g>
                  ))}
                </>
              )}

              {chartMode === 'tri' && (
                <>
                  <path d={triangularAreaPath} className="chart-area-fill chart-area-fill-animate" />
                  <path d={triangularLinePath} className="chart-line chart-line-tri chart-line-animate" pathLength="1" />
                  {points.map((point, index) => (
                    <g key={`tri-${point.labelDate.toISOString()}-${point.x}`}>
                      <circle
                        cx={point.x}
                        cy={point.y}
                        r="4"
                        className="chart-point chart-point-animate"
                        style={{ animationDelay: `${index * 45}ms` }}
                      />
                      <title>{`${point.labelDate.toISOString()}: ${point.count}`}</title>
                    </g>
                  ))}
                </>
              )}
            </g>

            {hoverZones.map((zone) => (
              <rect
                key={`hover-zone-${zone.index}`}
                x={zone.x}
                y={padding.top}
                width={zone.width}
                height={plotHeight}
                className="chart-hover-hit"
                onMouseEnter={() => setHoveredChartIndex(zone.index)}
              />
            ))}

            {hoveredSegment && hoveredPoint && (
              <g className="chart-hover-layer" pointerEvents="none">
                <line
                  x1={hoveredPoint.x}
                  y1={padding.top}
                  x2={hoveredPoint.x}
                  y2={padding.top + plotHeight}
                  className="chart-hover-guide"
                />
                <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r="6" className="chart-hover-focus-dot" />
                <rect x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight} rx="10" className="chart-hover-card" />
                <text x={tooltipX + 10} y={tooltipY + 20} className="chart-hover-title">
                  {hoverCountLabel}
                </text>
                <text x={tooltipX + 10} y={tooltipY + 38} className="chart-hover-subtitle">
                  {hoverRangeLabel}
                </text>
                <text x={tooltipX + 10} y={tooltipY + 55} className="chart-hover-subtitle">
                  {hoverDateLabel}
                </text>
              </g>
            )}

            {axisPoints.map((point, index) => (
              <text key={`x-label-${index}`} x={point.x} y={chartHeight - 14} textAnchor="middle" className="chart-axis-label">
                {formatSeriesLabel(point.labelDate)}
              </text>
            ))}
          </svg>
        </div>
        <div className="dashboard-click-chart-labels" aria-hidden="true">
          {axisPoints.map((point, index) => (
            <div key={`x-${point.labelDate.toISOString()}-${index}`} className="dashboard-click-chart-label-item">
              <span>{formatSeriesLabel(point.labelDate)}</span>
              <strong>{point.count}</strong>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLinkInformationPanel = () => {
    const appId = analyticsModalItem?.appId || '';
    const app = getAppById(appId);
    const supportsMessage = appId === 'whatsapp' || appId === 'telegram' || appId === 'messenger';
    const isWhatsapp = appId === 'whatsapp';
    const topCountry = Array.isArray(analytics?.countries) && analytics.countries.length > 0 ? analytics.countries[0] : null;
    const topCountryMeta = topCountry ? getCountryMeta(topCountry.country) : null;
    const createdAtValue = analyticsModalItem?.createdAt ? new Date(analyticsModalItem.createdAt).toLocaleString() : '---';
    const generatedLinkValue = analyticsModalItem?.shortUrl || '---';

    const whatsappPhoneDisplay =
      informationDetails.phoneNumber && informationDetails.phoneNumber !== '---'
        ? `${informationDetails.flag || '🌐'} ${informationDetails.countryName || '---'} (${informationDetails.dialCode || '---'}) ${informationDetails.phoneNumber}`
        : '---';

    const canCopyGeneratedLink = generatedLinkValue !== '---';

    return (
      <div className="dashboard-info-panel">
        <div className="dashboard-info-item">
          <span>Link Details</span>
          {editingOriginalLink ? (
            <div className="dashboard-info-edit-wrap">
              {isWhatsapp ? (
                <>
                  <label className="dashboard-info-field">
                    <span>Phone Number</span>
                    <div className="phone-input-row dashboard-info-phone-row">
                      <button
                        type="button"
                        className="country-picker-trigger"
                        onClick={() => setInfoCountryPickerOpen(true)}
                      >
                        {selectedInfoCountryOption ? (
                          <>
                            <img
                              src={selectedInfoCountryOption.flagUrl}
                              alt={selectedInfoCountryOption.name}
                              className="country-trigger-flag"
                            />
                            <span>{`${selectedInfoCountryOption.name} (${selectedInfoCountryOption.value})`}</span>
                          </>
                        ) : (
                          <span>Select Country Code</span>
                        )}
                      </button>
                      <input
                        value={phoneDraft}
                        onChange={(event) => setPhoneDraft(event.target.value)}
                        className="dashboard-info-input"
                        placeholder={app?.recipientPlaceholder || 'Phone Number'}
                      />
                    </div>
                  </label>
                  <label className="dashboard-info-field">
                    <span>Prefilled Text</span>
                    <input
                      value={textDraft}
                      onChange={(event) => setTextDraft(event.target.value)}
                      className="dashboard-info-input"
                      placeholder={app?.messagePlaceholder || 'Message text'}
                    />
                  </label>
                </>
              ) : (
                <>
                  <label className="dashboard-info-field">
                    <span>{app?.recipientLabel || 'Recipient'}</span>
                    <input
                      value={recipientDraft}
                      onChange={(event) => setRecipientDraft(event.target.value)}
                      className="dashboard-info-input"
                      placeholder={app?.recipientPlaceholder || 'Enter value'}
                    />
                  </label>
                  {supportsMessage && (
                    <label className="dashboard-info-field">
                      <span>Prefilled Text</span>
                      <input
                        value={textDraft}
                        onChange={(event) => setTextDraft(event.target.value)}
                        className="dashboard-info-input"
                        placeholder={app?.messagePlaceholder || 'Message text'}
                      />
                    </label>
                  )}
                </>
              )}
              <div className="dashboard-info-actions">
                <button
                  type="button"
                  className="dashboard-info-btn"
                  onClick={saveOriginalLinkChange}
                  disabled={!hasInformationChanges}
                >
                  Save
                </button>
                <button type="button" className="dashboard-info-btn ghost" onClick={cancelEditOriginalLink}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="dashboard-info-details-card">
              {isWhatsapp ? (
                <>
                  <div className="dashboard-info-details-row">
                    <span>Phone Number</span>
                    <strong>{whatsappPhoneDisplay}</strong>
                  </div>
                  <div className="dashboard-info-details-row">
                    <span>Prefilled Text</span>
                    <strong>{informationDetails.text || '---'}</strong>
                  </div>
                </>
              ) : (
                <>
                  <div className="dashboard-info-details-row">
                    <span>{app?.recipientLabel || 'Recipient'}</span>
                    <strong>{informationDetails.recipient || '---'}</strong>
                  </div>
                  {supportsMessage && (
                    <div className="dashboard-info-details-row">
                      <span>Prefilled Text</span>
                      <strong>{informationDetails.text || '---'}</strong>
                    </div>
                  )}
                </>
              )}
              <button type="button" className="dashboard-info-btn dashboard-info-edit-trigger" onClick={startEditOriginalLink}>
                <FiEdit2 />
                Edit
              </button>
            </div>
          )}
        </div>

        <div className="dashboard-info-item">
          <span>Generated Link</span>
          <div className="dashboard-generated-link-row">
            {generatedLinkValue === '---' ? (
              <strong>---</strong>
            ) : (
              <a href={generatedLinkValue} target="_blank" rel="noreferrer" className="dashboard-generated-link-text">
                {generatedLinkValue}
              </a>
            )}
            <button
              type="button"
              className={`dashboard-copy-link-btn ${generatedLinkCopied ? 'copied' : ''}`}
              onClick={copyGeneratedLink}
              disabled={!canCopyGeneratedLink}
              aria-label="Copy generated link"
              title={generatedLinkCopied ? 'Copied' : 'Copy link'}
            >
              {generatedLinkCopied ? <FiCheck /> : <FiCopy />}
            </button>
          </div>
        </div>

        <div className="dashboard-info-item">
          <span>Creation Time</span>
          <strong>{createdAtValue}</strong>
        </div>

        <div className="dashboard-info-item">
          <span>Top Country</span>
          <strong>
            {topCountryMeta
              ? `${topCountryMeta.name} (${Number(topCountry?.count || 0)} ${Number(topCountry?.count || 0) === 1 ? 'click' : 'clicks'})`
              : 'No country data yet'}
          </strong>
        </div>

        {linkInfoError && <p className="auth-error">{linkInfoError}</p>}

        {infoCountryPickerOpen && isWhatsapp && (
          <div className="country-modal-backdrop" onClick={() => setInfoCountryPickerOpen(false)}>
            <button
              type="button"
              className="country-modal-close"
              onClick={() => setInfoCountryPickerOpen(false)}
              aria-label="Close country picker"
            >
              <FiX />
            </button>

            <div className="country-modal-card" onClick={(event) => event.stopPropagation()}>
              <div className="country-modal-search">
                <FiSearch />
                <input
                  autoFocus
                  value={infoCountrySearch}
                  onChange={(event) => setInfoCountrySearch(event.target.value)}
                  placeholder="Search country or code"
                />
              </div>
              <div className="country-modal-list">
                {filteredInfoCountryOptions.map((option) => (
                  <button
                    key={`info-country-${option.countryCode}-${option.value}`}
                    type="button"
                    className={`country-option ${dialCodeDraft === option.value ? 'active' : ''}`}
                    onClick={() => {
                      setDialCodeDraft(option.value);
                      setInfoCountryPickerOpen(false);
                    }}
                  >
                    <span className="country-flag">
                      <img src={option.flagUrl} alt={option.name} loading="lazy" />
                    </span>
                    <span>{option.name}</span>
                    <span>{option.value}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAnalyticsPanelContent = () => {
    if (analyticsLoadingFor === analyticsOpenFor) {
      return (
        <div className="dashboard-skeleton-wrap">
          <div className="dashboard-skeleton dashboard-skeleton-title" />
          <div className="dashboard-skeleton dashboard-skeleton-row" />
          <div className="dashboard-skeleton dashboard-skeleton-row" />
          <div className="dashboard-skeleton dashboard-skeleton-row" />
          <div className="dashboard-skeleton dashboard-skeleton-chart" />
        </div>
      );
    }

    if (analyticsError) {
      return <p className="auth-error">{analyticsError}</p>;
    }

    if (!analytics) {
      return <p>No analytics data available.</p>;
    }

    if (activeAnalyticsTab === 'clicks') {
      return (
        <div className="dashboard-analytics-section">
          <div className="dashboard-kpi-row">
            <div className="dashboard-kpi-grid">
              <div className="dashboard-kpi-card">
                <span>Selected Range Clicks</span>
                <strong>{Number(analytics.clicks || 0)}</strong>
              </div>
              <div className="dashboard-kpi-card">
                <span>All Time Clicks</span>
                <strong>{Number(analytics.clicksAllTime || 0)}</strong>
              </div>
            </div>
            <div className="dashboard-chart-toggle" role="group" aria-label="Chart style">
              <button
                type="button"
                className={`dashboard-chart-toggle-btn ${chartMode === 'curve' ? 'active' : ''}`}
                onClick={() => setChartMode('curve')}
                aria-label="Curve graph"
                title="Curve graph"
              >
                <FiActivity />
              </button>
              <button
                type="button"
                className={`dashboard-chart-toggle-btn ${chartMode === 'corner' ? 'active' : ''}`}
                onClick={() => setChartMode('corner')}
                aria-label="Corner graph"
                title="Corner graph"
              >
                <FiCornerDownRight />
              </button>
              <button
                type="button"
                className={`dashboard-chart-toggle-btn ${chartMode === 'bars' ? 'active' : ''}`}
                onClick={() => setChartMode('bars')}
                aria-label="Vertical bar chart"
                title="Vertical bar chart"
              >
                <FiBarChart2 />
              </button>
              <button
                type="button"
                className={`dashboard-chart-toggle-btn ${chartMode === 'tri' ? 'active' : ''}`}
                onClick={() => setChartMode('tri')}
                aria-label="Triangular chart"
                title="Triangular chart"
              >
                <FiPlay />
              </button>
            </div>
          </div>
          {renderClicksGraph()}
        </div>
      );
    }

    if (activeAnalyticsTab === 'country') {
      return renderCountryBreakdown(Array.isArray(analytics.countries) ? analytics.countries : []);
    }

    if (activeAnalyticsTab === 'information') {
      return renderLinkInformationPanel();
    }

    return (
      <div className="dashboard-analytics-section">
        {renderBreakdown('Device Distribution', 'device', Array.isArray(analytics.devices) ? analytics.devices : [])}
        {renderBreakdown('OS Distribution', 'os', Array.isArray(analytics.os) ? analytics.os : [])}
        {renderBreakdown('Browser Distribution', 'browser', Array.isArray(analytics.browsers) ? analytics.browsers : [])}
      </div>
    );
  };

  if (!currentUser) {
    return (
      <section className="content-page">
        <div className="generator-card dashboard-card">
          <h2>My Dashboard</h2>
          <p>Please login to view your generated links.</p>
          <div className="generator-actions">
            <button type="button" onClick={onOpenAuth}>
              Login / Sign Up
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="content-page">
      <div className="generator-card dashboard-card">
        <div className="dashboard-top-row">
          <h2>My Dashboard</h2>
          <button
            type="button"
            className="dashboard-modal-updated-btn"
            onClick={refreshDashboard}
            disabled={loading}
            aria-label="Refresh dashboard links"
            title="Refresh dashboard links"
          >
            <span className="dashboard-modal-updated-label">
              <span className={`dashboard-modal-updated-dot ${dashboardDotStatus}`} aria-hidden="true" />
              Last updated: {withAgoSuffix(dashboardUpdatedSinceLabel)}
            </span>
            <FiRefreshCw className={loading ? 'spinning' : ''} />
          </button>
        </div>

        {loading && <p>Loading links...</p>}
        {error && <p className="auth-error">{error}</p>}

        {!loading && !error && allLinks.length === 0 && <p>No links found yet. Generate your first link to see it here.</p>}

        {!loading && allLinks.length > 0 && (
          <div className="dashboard-client-filters" aria-label="Dashboard client-side filters">
            <label>
              <span>App</span>
              <select value={appFilter} onChange={(event) => setAppFilter(event.target.value)}>
                <option value="all">All Apps</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="instagram">Instagram</option>
                <option value="messenger">Messenger</option>
                <option value="telegram">Telegram</option>
              </select>
            </label>
            <label>
              <span>Click State</span>
              <select value={clickFilter} onChange={(event) => setClickFilter(event.target.value)}>
                <option value="all">All Links</option>
                <option value="clicked">Clicked</option>
                <option value="not-clicked">Not Clicked</option>
              </select>
            </label>
          </div>
        )}

        {!loading && allLinks.length > 0 && filteredLinks.length === 0 && (
          <p>No links match the selected client-side filters.</p>
        )}

        {!loading && filteredLinks.length > 0 && (
          <div className="dashboard-table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>App</th>
                  <th>Short Link</th>
                  <th>Original Link</th>
                  <th>Clicks</th>
                  <th>Created</th>
                  <th>Analytics</th>
                </tr>
              </thead>
              <tbody>
                {filteredLinks.map((item) => (
                  <tr key={`${item.appId}-${item.shortCode}`} className="dashboard-row" onClick={() => openAnalytics(item)}>
                    <td>
                      <span className="dashboard-app-cell">
                        <span className={`dashboard-app-logo app-icon-${item.appId}`}>
                          {(() => {
                            const Icon = appIcons[item.appId] ?? FiGrid;
                            return <Icon />;
                          })()}
                        </span>
                        <span>{(getAppById(item.appId)?.name || item.appId || '').replace(/^./, (character) => character.toUpperCase())}</span>
                      </span>
                    </td>
                    <td>
                      <a href={item.shortUrl} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>
                        {item.shortCode}
                      </a>
                    </td>
                    <td>
                      <a href={item.originalLink} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>
                        Open
                      </a>
                    </td>
                    <td>{Number(item.clicks || 0)}</td>
                    <td>{new Date(item.createdAt).toLocaleString()}</td>
                    <td>
                      <button
                        type="button"
                        className="dashboard-viewmore-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          openAnalytics(item);
                        }}
                      >
                        View More
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {analyticsOpenFor && analyticsModalItem && (
        <div className="dashboard-modal-backdrop" onClick={closeAnalyticsModal}>
          <div className="dashboard-modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="dashboard-modal-actions">
              <button
                type="button"
                className="dashboard-modal-updated-btn"
                onClick={refreshAnalytics}
                disabled={analyticsLoadingFor === analyticsOpenFor}
                aria-label="Refresh analytics"
                title="Refresh analytics"
              >
                <span className="dashboard-modal-updated-label">
                  <span className={`dashboard-modal-updated-dot ${updatedDotStatus}`} aria-hidden="true" />
                  Last updated: {withAgoSuffix(updatedSinceLabel)}
                </span>
                <FiRefreshCw className={analyticsLoadingFor === analyticsOpenFor ? 'spinning' : ''} />
              </button>
              <button type="button" className="dashboard-modal-close" onClick={closeAnalyticsModal} aria-label="Close analytics modal">
                <FiX />
              </button>
            </div>

            <aside className="dashboard-modal-sidebar">
              <h3>{analyticsModalItem.shortCode}</h3>
              <p>{(getAppById(analyticsModalItem.appId)?.name || analyticsModalItem.appId || '').replace(/^./, (character) => character.toUpperCase())}</p>
              <button
                type="button"
                className={`dashboard-modal-tab ${activeAnalyticsTab === 'clicks' ? 'active' : ''}`}
                onClick={() => setActiveAnalyticsTab('clicks')}
              >
                Clicks
              </button>
              <button
                type="button"
                className={`dashboard-modal-tab ${activeAnalyticsTab === 'country' ? 'active' : ''}`}
                onClick={openCountryTab}
              >
                Country
              </button>
              <button
                type="button"
                className={`dashboard-modal-tab ${activeAnalyticsTab === 'deviceos' ? 'active' : ''}`}
                onClick={() => setActiveAnalyticsTab('deviceos')}
              >
                Device / OS
              </button>
              <button
                type="button"
                className={`dashboard-modal-tab ${activeAnalyticsTab === 'information' ? 'active' : ''}`}
                onClick={() => setActiveAnalyticsTab('information')}
              >
                Information
              </button>
            </aside>

            <section className={`dashboard-modal-content ${activeAnalyticsTab === 'information' ? 'information-mode' : ''}`}>
              {activeAnalyticsTab !== 'information' && (
                <div className="dashboard-filter-wrap">
                  <div className="dashboard-filter-row">
                    <button
                      type="button"
                      className={`dashboard-filter-btn ${activeRange === 'all' ? 'active' : ''}`}
                      onClick={() => applyRange('all')}
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className={`dashboard-filter-btn ${activeRange === '15m' ? 'active' : ''}`}
                      onClick={() => applyRange('15m')}
                    >
                      Last 15 minutes
                    </button>
                    <button
                      type="button"
                      className={`dashboard-filter-btn ${activeRange === '1h' ? 'active' : ''}`}
                      onClick={() => applyRange('1h')}
                    >
                      Last hour
                    </button>
                    <button
                      type="button"
                      className={`dashboard-filter-btn ${activeRange === '24h' ? 'active' : ''}`}
                      onClick={() => applyRange('24h')}
                    >
                      Last 24 hours
                    </button>
                    <button
                      type="button"
                      className={`dashboard-filter-btn dashboard-filter-custom-btn ${activeRange === 'custom' ? 'active' : ''}`}
                      onClick={toggleCustomDropdown}
                    >
                      <span>Custom</span>
                      <FiChevronDown />
                    </button>
                  </div>
                  {customDropdownOpen && (
                    <div className="dashboard-custom-range-card">
                      <div className="dashboard-custom-range">
                        <label>
                          <span>Start</span>
                          <input type="datetime-local" value={customStart} onChange={(event) => setCustomStart(event.target.value)} />
                        </label>
                        <label>
                          <span>End</span>
                          <input type="datetime-local" value={customEnd} onChange={(event) => setCustomEnd(event.target.value)} />
                        </label>
                      </div>
                      <button type="button" className="dashboard-custom-apply-btn" onClick={applyCustomRange}>
                        OK
                      </button>
                    </div>
                  )}
                </div>
              )}

              {activeAnalyticsTab === 'information' && <div className="dashboard-info-top-spacer" aria-hidden="true" />}

              {renderAnalyticsPanelContent()}
            </section>

            {confirmSaveOpen && (
              <div className="dashboard-confirm-backdrop" onClick={() => setConfirmSaveOpen(false)}>
                <div className="dashboard-confirm-card" onClick={(event) => event.stopPropagation()}>
                  <h4>Confirm Update</h4>
                  <p>Are you sure you want to update this link?</p>
                  <div className="dashboard-confirm-actions">
                    <button type="button" className="dashboard-info-btn ghost" onClick={() => setConfirmSaveOpen(false)}>
                      Cancel
                    </button>
                    <button type="button" className="dashboard-info-btn" onClick={confirmSaveOriginalLinkChange}>
                      Yes, Update
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function LinkGeneratorPage({ currentUser, onOpenAuth }) {
  const { appId } = useParams();
  const app = getAppById(appId ?? '');
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState('');
  const [dialCode, setDialCode] = useState('');
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');

  useEffect(() => {
    setGeneratedLink('');
    setGenerateError('');
  }, [appId, recipient, message, dialCode]);

  useEffect(() => {
    if (app?.id !== 'whatsapp') {
      return;
    }

    const storedCountry = localStorage.getItem(COUNTRY_STORAGE_KEY);
    if (storedCountry) {
      const matched = countryPhoneOptions.find((option) => option.countryCode === storedCountry);
      if (matched) {
        setDialCode(matched.value);
        return;
      }
    }

    const getCountry = async () => {
      try {
        const response = await fetch('https://api.country.is/');
        const data = await response.json();
        if (!data?.country) {
          return;
        }
        const countryCode = String(data.country).toUpperCase();
        const matched = countryPhoneOptions.find((option) => option.countryCode === countryCode);
        if (matched) {
          setDialCode(matched.value);
          localStorage.setItem(COUNTRY_STORAGE_KEY, countryCode);
        }
      } catch {
        // Intentionally silent fallback.
      }
    };

    getCountry();
  }, [app?.id]);

  useEffect(() => {
    if (app?.id !== 'whatsapp' || !dialCode) {
      return;
    }
    const matched = countryPhoneOptions.find((option) => option.value === dialCode);
    if (matched) {
      localStorage.setItem(COUNTRY_STORAGE_KEY, matched.countryCode);
    }
  }, [dialCode, app?.id]);

  if (!app) {
    return <Navigate to="/" replace />;
  }

  const fullRecipient = app.id === 'whatsapp' ? (dialCode ? `${dialCode}${recipient}` : '') : recipient;
  const effectiveMessage = app.id === 'instagram' ? '' : message;
  const generated = app.buildLink(fullRecipient, effectiveMessage);
  const previewName =
    app.id === 'whatsapp'
      ? `${dialCode || '+__'} ${recipient.trim() || app.recipientPlaceholder}`
      : recipient.trim() || app.recipientPlaceholder;
  const mockupInputText = message.trim();
  const MockupIcon = appIcons[app.id] ?? FiHome;
  const selectedCountry = countryPhoneOptions.find((option) => option.value === dialCode);
  const filteredCountryOptions = countryPhoneOptions.filter((option) => {
    const query = countrySearch.trim().toLowerCase();
    if (!query) {
      return true;
    }
    return (
      option.name.toLowerCase().includes(query) ||
      option.value.includes(query) ||
      option.countryCode.toLowerCase().includes(query)
    );
  });

  const generateShortLink = async () => {
    if (!currentUser) {
      onOpenAuth();
      return;
    }

    if (!generated) {
      setGenerateError('Please fill required fields before generating link.');
      return;
    }

    setGenerating(true);
    setGenerateError('');
    try {
      const response = await generateCloudflareLink({
        user: currentUser,
        appId: app.id,
        recipient: fullRecipient,
        message: effectiveMessage,
        originalLink: generated
      });
      setGeneratedLink(response?.shortUrl || '');
    } catch (requestError) {
      setGenerateError(requestError.message || 'Failed to generate link.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <section className="content-page">
      <div className="generator-layout">
        <div className="generator-card">
          <h2>{app.name} Link Generator</h2>

          {app.id === 'whatsapp' ? (
            <div className="form-field phone-group">
              <span>{app.recipientLabel}</span>
              <div className="phone-input-row">
                <button
                  type="button"
                  className="country-picker-trigger"
                  onClick={() => setCountryPickerOpen(true)}
                >
                  {selectedCountry ? (
                    <>
                      <img src={selectedCountry.flagUrl} alt={selectedCountry.name} className="country-trigger-flag" />
                      <span>{`${selectedCountry.name} (${selectedCountry.value})`}</span>
                    </>
                  ) : (
                    <span>Select Country Code</span>
                  )}
                </button>
                <input
                  value={recipient}
                  onChange={(event) => setRecipient(event.target.value)}
                  placeholder={app.recipientPlaceholder}
                />
              </div>
            </div>
          ) : (
            <label className="form-field">
              <span>{app.recipientLabel}</span>
              <input
                value={recipient}
                onChange={(event) => setRecipient(event.target.value)}
                placeholder={app.recipientPlaceholder}
              />
            </label>
          )}

          {app.id !== 'instagram' && (
            <label className="form-field">
              <span>Prefilled Text</span>
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder={app.messagePlaceholder}
                rows={4}
              />
            </label>
          )}

          <label className="form-field">
            <span>Generated Link</span>
            <input value={generatedLink} readOnly placeholder="Generated link will appear here..." />
          </label>

          {generateError && <p className="auth-error">{generateError}</p>}

          <div className="generator-actions">
            <a
              href={generatedLink || '#!'}
              target="_blank"
              rel="noreferrer"
              className={generatedLink ? '' : 'disabled-link'}
            >
              Preview
            </a>
            <button type="button" onClick={generateShortLink} disabled={generating || !currentUser}>
              {generating ? 'Generating...' : 'Generate Link'}
            </button>
          </div>
        </div>

        <aside className="phone-preview" aria-label="Live mobile preview">
          <div className="phone-notch" />
          <div className={`phone-screen mockup-${app.id}`}>
            <div className="mockup-header">
              <span className={`mockup-avatar mockup-avatar-${app.id}`}>
                <MockupIcon />
              </span>
              <div className="mockup-contact">
                <strong>{previewName}</strong>
                <span>online</span>
              </div>
            </div>

            <div className="mockup-chat-area">
              {app.id === 'instagram' && (
                <div className="mockup-note">Instagram does not support direct prefilled DM links.</div>
              )}
            </div>

            <div className="mockup-input-bar">
              <div className="mockup-text-input">{mockupInputText || 'Type a message here'}</div>
              <a
                href={generatedLink || '#!'}
                target="_blank"
                rel="noreferrer"
                className={generatedLink ? '' : 'disabled-link'}
                aria-label="Send link"
                title="Send link"
              >
                <FiSend />
              </a>
            </div>
          </div>
        </aside>
      </div>

      {app.id === 'whatsapp' && countryPickerOpen && (
        <div className="country-modal-backdrop" onClick={() => setCountryPickerOpen(false)}>
          <button
            type="button"
            className="country-modal-close"
            onClick={() => setCountryPickerOpen(false)}
            aria-label="Close country picker"
          >
            <FiX />
          </button>

          <div className="country-modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="country-modal-search">
              <FiSearch />
              <input
                autoFocus
                value={countrySearch}
                onChange={(event) => setCountrySearch(event.target.value)}
                placeholder="Search country or code"
              />
            </div>
            <div className="country-modal-list">
              {filteredCountryOptions.map((option) => (
                <button
                  key={`${option.countryCode}-${option.value}`}
                  type="button"
                  className={`country-option ${dialCode === option.value ? 'active' : ''}`}
                  onClick={() => {
                    setDialCode(option.value);
                    setCountryPickerOpen(false);
                  }}
                >
                  <span className="country-flag">
                    <img src={option.flagUrl} alt={option.name} loading="lazy" />
                  </span>
                  <span>{option.name}</span>
                  <span>{option.value}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function AppShell() {
  const { mode, toggleMode } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(auth.currentUser);
  const [toastMessage, setToastMessage] = useState('');
  const [toastClosing, setToastClosing] = useState(false);
  const toastCloseTimerRef = useRef(null);
  const toastRemoveTimerRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setCurrentUser(nextUser);

      if (!nextUser) {
        return;
      }

      syncCloudflareUser({ user: nextUser }).catch(() => {
        // Ignore sync errors here; user actions can retry sync via API calls.
      });
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    return () => {
      if (toastCloseTimerRef.current) {
        clearTimeout(toastCloseTimerRef.current);
      }
      if (toastRemoveTimerRef.current) {
        clearTimeout(toastRemoveTimerRef.current);
      }
    };
  }, []);

  const closeToast = () => {
    setToastClosing(true);
    if (toastCloseTimerRef.current) {
      clearTimeout(toastCloseTimerRef.current);
      toastCloseTimerRef.current = null;
    }
    if (toastRemoveTimerRef.current) {
      clearTimeout(toastRemoveTimerRef.current);
    }
    toastRemoveTimerRef.current = setTimeout(() => {
      setToastMessage('');
      setToastClosing(false);
      toastRemoveTimerRef.current = null;
    }, 320);
  };

  const showToast = (message) => {
    if (toastCloseTimerRef.current) {
      clearTimeout(toastCloseTimerRef.current);
    }
    if (toastRemoveTimerRef.current) {
      clearTimeout(toastRemoveTimerRef.current);
    }

    setToastClosing(false);
    setToastMessage(message);

    toastCloseTimerRef.current = setTimeout(() => {
      closeToast();
      toastCloseTimerRef.current = null;
    }, 2000);
  };

  const handleAuthSuccess = (message) => {
    setAuthOpen(false);
    navigate('/');
    showToast(message);
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  return (
    <div className="app-root">
      <div className="app-shell">
        <header className="top-nav">
          <button
            type="button"
            className="nav-arrow"
            aria-label="Toggle sidebar"
            onClick={() => setCollapsed((previous) => !previous)}
          >
            {collapsed ? <FiChevronRight /> : <FiChevronLeft />}
          </button>

          <div className="nav-actions">
            {!currentUser && (
              <button type="button" className="auth-nav-button" onClick={() => setAuthOpen(true)}>
                Login / Sign Up
              </button>
            )}
            <button
              type="button"
              className="nav-icon-button"
              onClick={toggleMode}
              aria-label="Toggle light and dark theme"
            >
              {mode === 'light' ? <FiMoon /> : <FiSun />}
            </button>
            <button type="button" className="nav-icon-button" onClick={() => navigate('/')} aria-label="Home">
              <FiHome />
            </button>
          </div>
        </header>

        <div className="app-body">
          <Sidebar
            collapsed={collapsed}
            currentUser={currentUser}
            onOpenAuth={() => setAuthOpen(true)}
            onOpenPersonalisation={() => navigate('/settings')}
            onOpenDashboard={() => navigate('/dashboard')}
            onLogout={handleLogout}
          />
          <main className="main-area">
            <div className="main-scroll">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/generator/:appId" element={<LinkGeneratorPage currentUser={currentUser} onOpenAuth={() => setAuthOpen(true)} />} />
                <Route
                  path="/dashboard"
                  element={<DashboardPage currentUser={currentUser} onOpenAuth={() => setAuthOpen(true)} onNotify={showToast} />}
                />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </main>
        </div>

        <AuthModal
          open={authOpen}
          onClose={() => setAuthOpen(false)}
          currentUser={currentUser}
          onAuthSuccess={handleAuthSuccess}
        />

        {toastMessage && (
          <div className={`auth-success-toast ${toastClosing ? 'closing' : ''}`} role="status" aria-live="polite">
            <p>{toastMessage}</p>
            <button type="button" aria-label="Dismiss success message" onClick={closeToast}>
              <FiX />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;